/** Matches aws-glo02-<group_name>-<platform_name>-<environment>@globe.com.ph (backend-aligned). */
const AWS_GLO02_DL_RE = /^aws-glo02-(.+)-([^@\s]+)@/i

export type ParsedAwsGlo02DistributionList = {
	groupName: string
	platformName: string
	envFromEmail: string
}

export function parseAwsGlo02DistributionList(
	distributionList: string | undefined | null
): ParsedAwsGlo02DistributionList | null {
	if (!distributionList || typeof distributionList !== 'string') return null
	const trimmed = distributionList.trim()
	const match = AWS_GLO02_DL_RE.exec(trimmed)
	if (!match) return null

	const groupPlatform = match[1].trim()
	const envFromEmail = match[2].trim().toLowerCase()
	if (!groupPlatform.includes('-')) {
		return {
			groupName: groupPlatform.toLowerCase(),
			platformName: '',
			envFromEmail
		}
	}

	const dashIndex = groupPlatform.indexOf('-')
	return {
		groupName: groupPlatform.slice(0, dashIndex).trim().toLowerCase(),
		platformName: groupPlatform.slice(dashIndex + 1).trim().toLowerCase(),
		envFromEmail
	}
}

/** VPC env segment for `{group}-{alias}-{env}-aws-vpc-1`. */
export function accountTypeToVpcEnvCode(
	accountType: string | undefined,
	options?: { isStgFlow?: boolean }
): string {
	if (options?.isStgFlow) return 'st'

	const normalized = String(accountType ?? '')
		.trim()
		.toLowerCase()
	if (normalized === 'production' || normalized === 'prod') return 'pd'
	if (normalized === 'sandbox') return 'sb'
	if (
		normalized === 'non-production' ||
		normalized === 'nonproduction' ||
		normalized === 'non-prod'
	) {
		return 'dv'
	}
	return ''
}

export function resolveTagScalar(value: unknown): string {
	if (value === undefined || value === null) return ''
	if (typeof value === 'object' && !Array.isArray(value) && 'value' in value) {
		return String((value as { value: unknown }).value ?? '').trim()
	}
	return String(value).trim()
}

/** `{group_name}-{system_alias}-{env}-aws-vpc-1` */
export function buildDefaultVpcName(params: {
	groupName?: string
	systemAlias?: unknown
	envCode?: string
	distributionList?: string
	isStgFlow?: boolean
	accountType?: string
}): string {
	const parsed = params.distributionList
		? parseAwsGlo02DistributionList(params.distributionList)
		: null
	const groupName = (params.groupName ?? parsed?.groupName ?? '').trim().toLowerCase()
	const systemAlias = resolveTagScalar(params.systemAlias).toLowerCase()
	const envCode =
		params.envCode?.trim().toLowerCase() ||
		accountTypeToVpcEnvCode(params.accountType, { isStgFlow: params.isStgFlow })

	if (!groupName || !systemAlias || !envCode) return ''
	return `${groupName}-${systemAlias}-${envCode}-aws-vpc-1`
}

export const PRIVATE_HOSTED_ZONE_DOMAIN_SUFFIX = '.globetel.cloud'

/** `{system_alias}-{env}.globetel.cloud` */
export function buildDefaultPrivateHostedZoneName(params: {
	systemAlias?: unknown
	envCode?: string
	isStgFlow?: boolean
	accountType?: string
}): string {
	const systemAlias = resolveTagScalar(params.systemAlias).toLowerCase()
	const envCode =
		params.envCode?.trim().toLowerCase() ||
		accountTypeToVpcEnvCode(params.accountType, { isStgFlow: params.isStgFlow })

	if (!systemAlias || !envCode) return ''
	return `${systemAlias}-${envCode}${PRIVATE_HOSTED_ZONE_DOMAIN_SUFFIX}`
}
